import React, { useState, useEffect } from 'react';
import { POD_COLLECTION } from '../config/collections';
import { useLanguage } from '../contexts/LanguageContext';
import { msconsService, MsconsSummary } from '../services/msconsService';
import CommonPageHeader from '../components/common/CommonPageHeader';
import LoadingScreen from '../components/common/LoadingScreen';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  ChartBarIcon, 
  CalendarIcon, 
  BoltIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  DocumentTextIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

const MsconsReadings: React.FC = () => {
  const { t } = useLanguage();
  const [summaries, setSummaries] = useState<MsconsSummary[]>([]);
  const [allSummaries, setAllSummaries] = useState<MsconsSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedPodIds, setSelectedPodIds] = useState<string>(''); // Vesszővel elválasztott POD ID-k
  const [availablePods, setAvailablePods] = useState<{id: string, name: string, provider: string}[]>([]);
  const [expandedPod, setExpandedPod] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [loadingPods, setLoadingPods] = useState(false);

  // POD lista betöltése
  useEffect(() => {
    loadAvailablePods();
  }, []);

  // NE töltse be automatikusan - csak manuálisan
  useEffect(() => {
    filterData();
  }, [selectedProvider, allSummaries]);

  const loadAvailablePods = async () => {
    try {
      setLoadingPods(true);
      console.log('📋 Loading available POD IDs from energy_pods collection...');
      
      const podsRef = collection(db, POD_COLLECTION);
      const podsSnapshot = await getDocs(podsRef);
      
      // Csak a document ID-kat töltjük be
      const pods = podsSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.id, // Csak az ID kell
        provider: '' // Nem kell
      }));
      
      setAvailablePods(pods.sort((a, b) => a.id.localeCompare(b.id)));
      console.log(`✅ Loaded ${pods.length} POD IDs`);
    } catch (error) {
      console.error('❌ Error loading POD IDs:', error);
    } finally {
      setLoadingPods(false);
    }
  };

  const loadMsconsData = async () => {
    try {
      setLoading(true);
      setHasLoaded(true);
      const startDate = new Date(selectedYear, 0, 1);
      const endDate = new Date(selectedYear, 11, 31);
      
      // POD ID-k feldolgozása (vesszővel elválasztva)
      const podIds = selectedPodIds
        .split(',')
        .map(id => id.trim())
        .filter(id => id.length > 0);
      
      console.log('🔍 Loading MSCONS data for year:', selectedYear);
      console.log('📅 Date range:', startDate, 'to', endDate);
      console.log('🎯 Selected POD IDs:', podIds.length > 0 ? podIds : 'ALL');
      
      const data = await msconsService.getAllMsconsSummariesForPods(
        podIds.length > 0 ? podIds : undefined,
        startDate,
        endDate
      );
      
      console.log('📊 Loaded summaries:', data.length);
      console.log('🏢 Providers found:', Array.from(new Set(data.map(s => s.provider))));
      
      setAllSummaries(data);
    } catch (error) {
      console.error('❌ Error loading MSCONS data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let filtered = allSummaries;
    
    if (selectedProvider) {
      filtered = filtered.filter(s => s.provider === selectedProvider);
    }
    
    setSummaries(filtered);
  };

  const providers = Array.from(new Set(allSummaries.map(s => s.provider)));
  const totalConsumption = summaries.reduce((sum, s) => sum + s.totalConsumption, 0);
  const totalPods = summaries.length;

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <CommonPageHeader
        title="MSCONS Fogyasztási Adatok"
        subtitle="Szolgáltatók által számolt havi összesített fogyasztások"
        icon={DocumentTextIcon}
      />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Szűrők */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <FunnelIcon className="h-6 w-6 text-blue-500" />
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Szűrők és Keresés
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                📅 Év
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all"
              >
                {[2025, 2024, 2023].map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                🔄 Manuális betöltés
              </label>
              <button
                onClick={loadMsconsData}
                disabled={loading || !selectedPodIds}
                className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all transform hover:scale-105 disabled:transform-none"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Betöltés...
                  </span>
                ) : (
                  hasLoaded ? 'Újratöltés' : 'Több POD betöltése'
                )}
              </button>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                🏢 Szolgáltató
              </label>
              <select
                value={selectedProvider}
                onChange={(e) => setSelectedProvider(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!hasLoaded}
              >
                <option value="">Összes szolgáltató</option>
                {providers.map(provider => (
                  <option key={provider} value={provider}>
                    {provider}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                🎯 POD ID-k (vesszővel elválasztva vagy válassz a listából)
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={selectedPodIds}
                  onChange={(e) => setSelectedPodIds(e.target.value)}
                  placeholder="pl: HU000110-11-S00000000000000004463, HU000110-11-S00000000000000015726"
                  className="flex-1 px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all placeholder:text-gray-400"
                />
                <select
                  onChange={async (e) => {
                    const podId = e.target.value;
                    if (podId) {
                      // POD kiválasztva - automatikus betöltés
                      setSelectedPodIds(podId);
                      e.target.value = '';
                      
                      // Automatikus betöltés
                      try {
                        setLoading(true);
                        setHasLoaded(true);
                        const startDate = new Date(selectedYear, 0, 1);
                        const endDate = new Date(selectedYear, 11, 31);
                        
                        console.log('🎯 POD kiválasztva, automatikus betöltés:', podId);
                        
                        const data = await msconsService.getAllMsconsSummariesForPods(
                          [podId],
                          startDate,
                          endDate
                        );
                        
                        setAllSummaries(data);
                      } catch (error) {
                        console.error('❌ Error loading MSCONS data:', error);
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  className="w-72 px-4 py-3 rounded-lg border-2 border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loadingPods || loading}
                >
                  <option value="">Válassz POD-ot...</option>
                  {availablePods.map(pod => (
                    <option key={pod.id} value={pod.id}>
                      {pod.id}
                    </option>
                  ))}
                </select>
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                {loadingPods ? (
                  <>
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    POD lista betöltése...
                  </>
                ) : (
                  <>
                    ✅ {availablePods.length} POD elérhető - <strong>Dropdown: automatikus betöltés</strong> | Input: több POD vesszővel
                  </>
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Összesítő kártyák */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Összes fogyasztás
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                  {totalConsumption.toLocaleString('hu-HU')} kWh
                </p>
              </div>
              <BoltIcon className="h-12 w-12 text-yellow-500" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Mérőpontok száma
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                  {totalPods}
                </p>
              </div>
              <ChartBarIcon className="h-12 w-12 text-blue-500" />
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Átlagos havi fogyasztás
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                  {totalPods > 0 
                    ? (totalConsumption / totalPods).toLocaleString('hu-HU', { maximumFractionDigits: 0 })
                    : 0
                  } kWh
                </p>
              </div>
              <CalendarIcon className="h-12 w-12 text-green-500" />
            </div>
          </div>
        </div>

        {/* MSCONS adatok táblázat */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Havi MSCONS Fogyasztások ({summaries.length} mérőpont)
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    POD ID / Név
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Szolgáltató
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Hónapok száma
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Összes fogyasztás
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Havi átlag
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Részletek
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {summaries.map((summary) => (
                  <React.Fragment key={summary.podId}>
                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {summary.podId}
                        </div>
                        {summary.podName && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {summary.podName}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {summary.provider}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                        {summary.monthlyReadings.length}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {summary.totalConsumption.toLocaleString('hu-HU')} kWh
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {summary.averageMonthly.toLocaleString('hu-HU', { maximumFractionDigits: 0 })} kWh
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => setExpandedPod(expandedPod === summary.podId ? null : summary.podId)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        >
                          {expandedPod === summary.podId ? '▼' : '▶'}
                        </button>
                      </td>
                    </tr>
                    
                    {expandedPod === summary.podId && (
                      <tr>
                        <td colSpan={6} className="px-6 py-4 bg-gray-50 dark:bg-gray-900">
                          <div className="space-y-2">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                              Havi bontás:
                            </h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              {summary.monthlyReadings
                                .sort((a, b) => b.month.localeCompare(a.month))
                                .map((reading) => (
                                  <div
                                    key={reading.month}
                                    className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
                                  >
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      {msconsService.getMonthName(reading.month)}
                                    </div>
                                    <div className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                                      {reading.consumption.toLocaleString('hu-HU')} kWh
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {summaries.length === 0 && !hasLoaded && (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                Kattints az "Adatok betöltése" gombra
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Az MSCONS adatok manuális betöltésre várnak. Válaszd ki az évet és kattints a betöltés gombra.
              </p>
            </div>
          )}
          
          {summaries.length === 0 && hasLoaded && (
            <div className="text-center py-12">
              <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                Nincs MSCONS adat
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                A kiválasztott szűrőkkel nem található MSCONS fogyasztási adat.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MsconsReadings;
